/**
 * The Lecture Record — SRS I5, docs/06-UI-SPEC.md §11.
 *
 * Every lesson a teacher has taught, in whichever of the three views they prefer.
 * Search, the quick filters and the chips arrive at step 7.5; opening a lesson to
 * carry on teaching it arrives at 7.6.
 *
 * The view they chose is remembered in this browser, because a teacher who prefers
 * the big previews prefers them tomorrow as well (§11.1).
 */

import { useEffect, useState } from 'react';

import {
  fetchLectures,
  resumeLecture,
  setLectureStarred,
  type LectureRecord,
} from '../api.js';
import { HomeHeader } from '../components/HomeHeader.js';
import { DetailsTable } from '../components/lectures/DetailsTable.js';
import { FilterBar } from '../components/lectures/FilterBar.js';
import { IconGrid } from '../components/lectures/IconGrid.js';
import {
  filterQuery,
  isUnfiltered,
  NO_FILTERS,
  type LectureFilters,
} from '../lecture-filters.js';
import { LessonsEmptyIcon } from '../assets/icons.js';
import {
  DEFAULT_SORT,
  nextSort,
  sortLectures,
  type Lecture,
  type LectureColumn,
  type LectureView,
  type SortOrder,
} from '../lectures.js';
import { navigate } from '../router.js';
import { signOut, useTeacherSession } from '../session.js';

const VIEW_STORAGE_KEY = 'opal.lectures.view';

const VIEWS: readonly { readonly value: LectureView; readonly label: string }[] = [
  { value: 'details', label: 'Details' },
  { value: 'large', label: 'Large icons' },
  { value: 'extraLarge', label: 'Extra large icons' },
];

/** The view this teacher last chose in this browser, or the list. */
function rememberedView(): LectureView {
  try {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return VIEWS.some((view) => view.value === stored) ? (stored as LectureView) : 'details';
  } catch {
    // A browser with storage turned off still gets a Lecture Record.
    return 'details';
  }
}

function rememberView(view: LectureView): void {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    // Not being able to remember the choice is not a reason to refuse to make it.
  }
}

function ViewSwitcher({
  view,
  onChange,
}: {
  readonly view: LectureView;
  readonly onChange: (view: LectureView) => void;
}) {
  return (
    <div className="flex items-center gap-2" role="group" aria-label="View">
      {VIEWS.map((choice) => (
        <button
          key={choice.value}
          type="button"
          aria-pressed={view === choice.value}
          onClick={() => {
            onChange(choice.value);
          }}
          className={`h-touch rounded-md border px-3 text-sm font-medium transition duration-fast ease-out ${
            view === choice.value
              ? 'border-primary bg-primary-subtle text-text-primary'
              : 'border-border bg-surface text-text-secondary hover:bg-surface-hover'
          }`}
        >
          {choice.label}
        </button>
      ))}
    </div>
  );
}

/** The title row: what this screen is, and which of the three views is showing. */
function RecordTitle({
  view,
  onChange,
}: {
  readonly view: LectureView;
  readonly onChange: (view: LectureView) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <h1 className="text-xl font-semibold text-text-primary">Lecture Record</h1>
      <ViewSwitcher
        view={view}
        onChange={(chosen) => {
          onChange(chosen);
          rememberView(chosen);
        }}
      />
    </div>
  );
}

/** The header. Its Lecture Record button goes back home, since this is that screen. */
function RecordHeader({ email }: { readonly email: string }) {
  return (
    <HomeHeader
      email={email}
      onOpenLectureRecord={() => {
        navigate('/home');
      }}
      onSignOut={() => {
        void signOut().then(() => {
          navigate('/');
        });
      }}
    />
  );
}

/** What is happening while a lesson is being opened, and what went wrong if it did. */
function ResumeStatus({
  opening,
  problem,
}: {
  readonly opening: string | null;
  readonly problem: string | null;
}) {
  return (
    <>
      {opening === null ? null : (
        <p role="status" className="text-base text-text-secondary">
          {opening}
        </p>
      )}
      {problem === null ? null : <p className="text-base text-danger">{problem}</p>}
    </>
  );
}

/** Nothing to show, and why — a search that found nothing is not an empty record. */
function EmptyState({
  problem,
  narrowed,
}: {
  readonly problem: string | null;
  readonly narrowed: boolean;
}) {
  const nothingFound = narrowed
    ? 'No lessons match what you are looking for'
    : 'Your finished lessons will appear here';

  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-surface p-12 text-center">
      <span className="text-text-placeholder">
        <LessonsEmptyIcon size={40} />
      </span>
      <p className="text-base text-text-secondary">{problem ?? nothingFound}</p>
    </div>
  );
}

/**
 * Reads the teacher's lessons, and reads them again whenever they narrow the list.
 *
 * The narrowing is done by the database, so what comes back is already the answer —
 * which is what keeps a year of lessons feeling the same size as a week of them.
 */
function useLectures(signedIn: boolean, filters: LectureFilters) {
  const [record, setRecord] = useState<LectureRecord>({
    lectures: [],
    topics: [],
    students: [],
  });
  const [problem, setProblem] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const query = filterQuery(filters);

  useEffect(() => {
    if (!signedIn) return;
    let current = true;
    void fetchLectures(query).then((result) => {
      // An answer to a search the teacher has already typed past is not an answer.
      if (!current) return;
      if (result.ok) {
        setRecord(result.value);
        setProblem(null);
      } else {
        setProblem(result.message);
      }
      setLoading(false);
    });
    return () => {
      current = false;
    };
  }, [signedIn, query]);

  return { record, setRecord, problem, loading };
}

/**
 * Opens a finished lesson again — docs/06-UI-SPEC.md §11.3.
 *
 * A lesson from inside the ninety-day window opens straight away. An older one has to
 * come back from Drive first, and §11.3 asks for that to be said rather than shown as
 * a pause with no explanation.
 */
function useResume() {
  const [opening, setOpening] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  return {
    opening,
    problem,
    open: (lecture: Lecture) => {
      if (opening !== null) return;
      setOpening(lecture.isHot ? 'Opening your lesson…' : 'Loading from Drive…');
      setProblem(null);
      void resumeLecture(lecture.id).then((result) => {
        if (!result.ok) {
          setOpening(null);
          setProblem(result.message);
          return;
        }
        navigate('/board', {
          topic: result.value.topic ?? '',
          code: result.value.roomCode,
          board: result.value.boardId,
        });
      });
    },
  };
}

/** Turns the star on or off, on screen first so it never feels slow. */
function useStar(
  record: LectureRecord,
  setRecord: (record: LectureRecord) => void,
): (lecture: Lecture) => void {
  return (lecture) => {
    const starred = !lecture.isStarred;
    setRecord({
      ...record,
      lectures: record.lectures.map((one) =>
        one.id === lecture.id ? { ...one, isStarred: starred } : one,
      ),
    });
    void setLectureStarred(lecture.id, starred);
  };
}

function Results({
  lectures,
  view,
  sort,
  onSort,
  onOpen,
  onStar,
}: {
  readonly lectures: readonly Lecture[];
  readonly view: LectureView;
  readonly sort: SortOrder;
  readonly onSort: (column: LectureColumn) => void;
  readonly onOpen: (lecture: Lecture) => void;
  readonly onStar: (lecture: Lecture) => void;
}) {
  if (view === 'details') {
    return (
      <DetailsTable
        lectures={lectures}
        sort={sort}
        onSort={onSort}
        onOpen={onOpen}
        onStar={onStar}
      />
    );
  }
  return <IconGrid lectures={lectures} size={view} onOpen={onOpen} onStar={onStar} />;
}

export function LecturesPage() {
  const session = useTeacherSession();
  const signedIn = session.status === 'signed-in';
  const [filters, setFilters] = useState<LectureFilters>(NO_FILTERS);
  const { record, setRecord, problem, loading } = useLectures(signedIn, filters);
  const [view, setView] = useState<LectureView>(rememberedView);
  const [sort, setSort] = useState<SortOrder>(DEFAULT_SORT);
  const toggleStar = useStar(record, setRecord);
  const resume = useResume();

  useEffect(() => {
    if (session.status === 'signed-out') navigate('/teacher');
  }, [session.status]);

  const sorted = sortLectures(record.lectures, sort);

  return (
    <div className="min-h-screen bg-surface-sunken">
      <RecordHeader email={signedIn ? session.teacher.email : ''} />
      <main className="mx-auto flex max-w-[1200px] flex-col gap-6 p-8 max-[640px]:p-4">
        <RecordTitle view={view} onChange={setView} />

        <FilterBar
          filters={filters}
          topics={record.topics}
          students={record.students}
          onChange={setFilters}
        />

        <ResumeStatus opening={resume.opening} problem={resume.problem} />

        {loading ? (
          <p className="text-base text-text-secondary">Opening your lessons…</p>
        ) : sorted.length === 0 ? (
          <EmptyState problem={problem} narrowed={!isUnfiltered(filters)} />
        ) : (
          <Results
            lectures={sorted}
            view={view}
            sort={sort}
            onSort={(column) => {
              setSort(nextSort(sort, column));
            }}
            onStar={toggleStar}
            onOpen={resume.open}
          />
        )}
      </main>
    </div>
  );
}
